import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useBranding } from '../contexts/BrandingContext'
import NotificationsDropdown from './NotificationsDropdown'
import ImportNotificationBadge from './imports/ImportNotificationBadge'
import { TenantSwitcher } from './TenantSwitcher/TenantSwitcher'

const Navbar = ({ onMenuToggle, sidebarOpen, onOpenProfile, onOpenNotificationPrefs, onOpenMfaSettings }) => {
  const { t } = useTranslation('common')
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const menuCloseTimeoutRef = useRef(null)
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const { branding, tenantName } = useBranding()

  // Get user role from the user object
  const userRole = user?.role

  // The presigned logoUrl is session-cached for up to an hour (#34) - if it
  // 404s (expired, or the object was removed) fall back rather than showing
  // a broken image. Reset whenever the URL changes so a fresh one gets a
  // fresh chance to load.
  const [logoLoadError, setLogoLoadError] = useState(false)
  useEffect(() => {
    setLogoLoadError(false)
  }, [branding?.logoUrl])
  const hasValidLogo = Boolean(branding?.logoUrl) && !logoLoadError
  // White-label with no logo (or a broken one) hides the CoFormIt mark
  // entirely rather than falling back to it - that fallback would defeat
  // the point of hiding CoFormIt branding.
  const showLogoIcon = hasValidLogo || !branding?.hideBranding

  // Close menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    // Use timeout to avoid immediate close from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  // Handle mouse enter on user menu - cancel any pending close
  const handleMenuMouseEnter = useCallback(() => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
  }, [])

  // Handle mouse leave on user menu - start 2 second close timer
  const handleMenuMouseLeave = useCallback(() => {
    if (userMenuOpen) {
      menuCloseTimeoutRef.current = setTimeout(() => {
        setUserMenuOpen(false)
        menuCloseTimeoutRef.current = null
      }, 2000)
    }
  }, [userMenuOpen])

  const handleLogout = () => {
    navigate('/logout')
  }

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <button
            className="mobile-menu-btn"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
          <Link to="/dashboard" className="logo">
            {showLogoIcon && (
              <img
                src={hasValidLogo ? branding.logoUrl : '/logo-icon.svg'}
                alt=""
                className="logo-icon"
                onError={() => setLogoLoadError(true)}
              />
            )}
            <span className="logo-text">
              {branding?.hideBranding ? (tenantName || t('labels.user')) : 'CoFormIt'}
            </span>
          </Link>
        </div>

        <div className="header-right">
          <TenantSwitcher />
          <a href="/help" className="help-link" target="_blank" rel="noopener noreferrer">
            <span className="help-icon">?</span>
            <span className="help-text">{t('navigation.help')}</span>
          </a>
          <ImportNotificationBadge />
          <NotificationsDropdown />
          <div
            className="user-menu"
            ref={userMenuRef}
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
          >
            <button
              className="user-menu-button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <span className="user-avatar">👤</span>
              <span className="user-email">
                {profile?.firstName ? t('greeting', { name: profile.firstName }) : user?.signInDetails?.loginId || profile?.email || t('labels.user')}
              </span>
              <span className="dropdown-arrow">{userMenuOpen ? '▲' : '▼'}</span>
            </button>

            {userMenuOpen && (
              <div className="user-menu-dropdown">
                <div className="user-info">
                  <div className="user-email-full">{profile?.email || user?.signInDetails?.loginId}</div>
                  <div className="user-role">{userRole || 'user'}</div>
                </div>
                <hr />
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setUserMenuOpen(false)
                    onOpenProfile?.()
                  }}
                >
                  {t('profile.editTitle')}
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setUserMenuOpen(false)
                    onOpenNotificationPrefs?.()
                  }}
                >
                  {t('navigation.notificationPrefs')}
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setUserMenuOpen(false)
                    onOpenMfaSettings?.()
                  }}
                >
                  {t('navigation.securitySettings')}
                </button>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                >
                  {t('buttons.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Navbar
